/**
 * Learn hub — client-side category filter + numbered pagination.
 * All articles are server-rendered up front (in curated Figma order); a "page" is
 * just which slice of the already-filtered set is visible, driven by the shared
 * `window.renderPagination` (see assets/theme.js — never `import` it, see the note there).
 * Page changes sync to `?page=` so browser back/forward restores the list position.
 */
if (!customElements.get('learn-hub')) {
  customElements.define(
    'learn-hub',
    class LearnHub extends HTMLElement {
      connectedCallback() {
        this.filterButtons = Array.from(this.querySelectorAll('[data-filter]'));
        this.articles = Array.from(this.querySelectorAll('[data-learn-article]'));
        this.paginationContainer = this.querySelector('[data-learn-pagination]');
        this.grid = this.querySelector('[data-learn-grid]');
        this.filterEmptyMessage = this.querySelector('[data-learn-filter-empty]');
        this.targetPerPage = Number(this.dataset.perPage) || 11;
        this.activeFilter = 'all';
        this.currentPage = this.readPageFromUrl();

        this.onFilterClick = this.onFilterClick.bind(this);
        this.onPageChange = this.onPageChange.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onPopState = this.onPopState.bind(this);

        for (const button of this.filterButtons) {
          button.addEventListener('click', this.onFilterClick);
        }

        window.addEventListener('resize', this.onResize);
        window.addEventListener('popstate', this.onPopState);

        this.render();
      }

      disconnectedCallback() {
        for (const button of this.filterButtons) {
          button.removeEventListener('click', this.onFilterClick);
        }

        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('popstate', this.onPopState);
      }

      readPageFromUrl() {
        const page = Number(new URLSearchParams(window.location.search).get('page'));

        return Number.isFinite(page) && page > 0 ? page : 1;
      }

      updateUrl(page, {replace = false} = {}) {
        const url = new URL(window.location.href);

        if (page <= 1) {
          url.searchParams.delete('page');
        } else {
          url.searchParams.set('page', String(page));
        }

        const state = {learnHubPage: page};
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;

        if (replace) {
          window.history.replaceState(state, '', nextUrl);
        } else {
          window.history.pushState(state, '', nextUrl);
        }
      }

      onPopState() {
        this.currentPage = this.readPageFromUrl();
        this.render();
      }

      /** Grid columns per Figma breakpoint (`assets/section-learn-hub.css`): 1 below 990px, 3 from 990px up. */
      getColumns() {
        return window.matchMedia('(min-width: 990px)').matches ? 3 : 1;
      }

      /**
       * Rounds the configured "articles per page" down to a full multiple of the current
       * column count, so only the true last page can ever end on a partial row — an
       * earlier page never shows a short row with dead grid space before it (QI: blank
       * space on the grid). The remainder always lands on the final page instead.
       */
      getPerPage() {
        const columns = this.getColumns();

        return Math.max(columns, Math.floor(this.targetPerPage / columns) * columns);
      }

      onResize() {
        const nextPerPage = this.getPerPage();

        if (nextPerPage !== this.lastPerPage) {
          this.render();
        }
      }

      onFilterClick(event) {
        const button = event.currentTarget;
        const filter = button.dataset.filter;
        if (!filter) {
          return;
        }

        this.activeFilter = filter;
        this.currentPage = 1;
        this.setActiveFilter(button);
        this.updateUrl(1, {replace: true});
        this.render();
      }

      onPageChange(page) {
        this.currentPage = page;
        this.updateUrl(page);
        this.render();
        window.revealListStart?.(this.grid);
      }

      setActiveFilter(activeButton) {
        for (const button of this.filterButtons) {
          const isActive = (button === activeButton);
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        }
      }

      getMatchingArticles() {
        if (this.activeFilter === 'all') {
          return this.articles;
        }

        return this.articles.filter((article) => article.dataset.category === this.activeFilter);
      }

      render() {
        const perPage = this.getPerPage();
        this.lastPerPage = perPage;

        const matching = this.getMatchingArticles();
        const totalPages = Math.max(1, Math.ceil(matching.length / perPage));
        this.currentPage = Math.min(this.currentPage, totalPages);

        for (const article of this.articles) {
          article.hidden = true;
        }

        const start = (this.currentPage - 1) * perPage;
        for (let index = start; index < start + perPage && index < matching.length; index += 1) {
          matching[index].hidden = false;
        }

        if (this.filterEmptyMessage) {
          this.filterEmptyMessage.hidden = (matching.length > 0);
        }

        window.renderPagination?.(this.paginationContainer, {
          currentPage: this.currentPage,
          totalPages,
          onChange: this.onPageChange,
        });
      }
    },
  );
}
